import {
  disciplineMetadata,
  ageCategoryMetadata,
  weightCategoryMetadata,
} from "./custom-disciplines/category-metadata.js";
import { athletes, groups, meets } from "@prisma-local/client";
import { Decimal } from "@prisma-local/client/runtime/library";
import { AgeCategory, Prisma } from "@prisma-remote/client";
import { format, isValid, parse } from "date-fns";
import { Server, Socket } from "socket.io";
import { customDisciplineGroupIsOver } from "./custom-disciplines/group-snapshot.js";
import {
  getAthletesByCategory,
  getCompetitionFormatById,
  getCurrentAthlete,
  getCurrentLift,
} from "./index.js";
import {
  DEFAULT_LEVEL_NAME_FALLBACK,
  resolveDefaultLevelName,
} from "./federation-config.js";
import {
  AthletesModel,
  AthleteWithAll,
  AutoField,
  CatAgeModel,
  Exercises,
  exercisesByFormat,
  GroupModel,
  GroupWithAll,
  LevelRemoteWithAll,
  LiftField,
  LiftStatus,
  MeetModel,
  MeetWithAll,
  NiveauxModel,
  OverturnColorField,
  OverturnField,
  RecordModel,
  RecordRemoteWithAll,
  RefereeColorField,
  SyncMeta,
} from "./types.js";
import { isSameRankingCategory } from "./ranking-category.js";

export const formatDate = (date: Date): string => {
  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Formats a date (or Date.now() timestamp) to DDMMYYYYmmss format.
 * Note: This format excludes hours. If you need hours, use formatDateDDMMYYYYHHmmss instead.
 * @param timestamp - Date object or timestamp from Date.now()
 * @returns Formatted string in DDMMYYYYmmss format (e.g., "251220241430" for Dec 25, 2024 at 14:30:xx)
 */
export const formatDateDDMMYYYYmmss = (
  timestamp: Date | number = Date.now(),
): string => {
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return format(date, "ddMMyyyymmss");
};

export const getExercise = (lift: LiftField) => {
  return lift.replace(/\d/g, "");
};

/**
 * Formats bytes into human-readable string with units.
 */
export const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"] as const;
  const exp = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exp);
  return `${value.toFixed(2)} ${units[exp]}`;
};

export const getResultIfSuccessful = (
  currentAthlete: AthletesModel,
  athletes: AthletesModel[],
  currentLift: LiftField,
  isNational = false,
) => {
  // Create a copy of the athlete with successful current lift
  currentAthlete = JSON.parse(JSON.stringify(currentAthlete));
  athletes = JSON.parse(
    JSON.stringify(athletes.filter((a) => a.isDisqualified === false)),
  );
  const athleteWithSuccess = {
    ...currentAthlete,
    [currentLift]: currentAthlete[currentLift],
    [getStatusField(currentLift)]: LiftStatus.LIFT,
  } as AthletesModel;

  const total = calculateTotal(athleteWithSuccess, false);
  // Convert to number to avoid Decimal issues in Electron context
  athleteWithSuccess.totalkg = Number(total) as any;
  athletes.forEach((a) => {
    if (a.id === athleteWithSuccess.id) {
      Object.assign(a, { totalkg: athleteWithSuccess.totalkg?.toString() });
    }
  });
  // Get athletes in same weight class and create hypothetical success versions
  const rankedAthlete = updateClassment(
    athletes,
    athleteWithSuccess,
    isNational,
  );
  // Find place of athlete
  return rankedAthlete.updatedAthlete;
};

export const isGroupOver = (group: GroupModel) => {
  if (
    group.customDisciplineGroupId ||
    group.competitionFormat?.startsWith("CUSTOM:")
  )
    return (
      group.customIsOver ??
      customDisciplineGroupIsOver(group.customDisciplineGroup)
    );
  if (group.athletes == undefined) return false;
  return (
    group.athletes.filter(
      (a) =>
        a.bench1Status == LiftStatus.ANNONCED ||
        a.bench2Status == LiftStatus.ANNONCED ||
        a.bench3Status == LiftStatus.ANNONCED ||
        a.squat1Status == LiftStatus.ANNONCED ||
        a.squat3Status == LiftStatus.ANNONCED ||
        a.squat2Status == LiftStatus.ANNONCED ||
        a.deadlift1Status == LiftStatus.ANNONCED ||
        a.deadlift2Status == LiftStatus.ANNONCED ||
        a.deadlift3Status == LiftStatus.ANNONCED,
    ).length == 0
  );
};

export const isMeetOver = (meet: MeetModel) => {
  return meet.groups
    .filter(
      (group) =>
        group.customDisciplineGroupId ||
        group.competitionFormat?.startsWith("CUSTOM:") ||
        group.athletes !== undefined,
    )
    .every(isGroupOver);
};

export const groupWithAllToGroupModel = (
  group: GroupWithAll,
  selected: boolean = false,
): GroupModel => {
  return {
    assesseur1: group.referee1,
    assesseur2: group.referee2,
    chefPlateau: group.centralReferee,
    id: group.id,
    meet_id: 0,
    nom: group.name,
    observations: group.observations,
    secretaire: group.secretary,
    athletes: group.athletes.map((a) => athleteWithAllToAthleteModel(a)),
    selected: selected,
    share: group.share,
    competitionFormat: group.competitionFormat || "FULL_POWER",
    customDisciplineGroupId: group.customDisciplineGroupId ?? null,
    customDisciplineGroup: group.customDisciplineGroup,
    customIsOver:
      group.customIsOver ??
      customDisciplineGroupIsOver(group.customDisciplineGroup),
    groupNumber: group.groupNumber,
    plateauNumber: group.plateauNumber,
    jury: group.jury,
  };
};

/**
 * Turns an API-relative public asset path into an absolute URL when a base is provided.
 *
 * @param value - Stored asset path or absolute URL.
 * @param apiBaseUrl - Optional API origin (e.g. http://localhost:3001).
 * @returns Absolute URL, original absolute value, or null.
 */
export function resolvePublicAssetUrl(
  value: string | null | undefined,
  apiBaseUrl?: string,
): string | null {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (!apiBaseUrl || !value.startsWith("/")) return value;
  return `${apiBaseUrl.replace(/\/$/, "")}${value}`;
}

export const recordRemoteToRecordLocal = (
  records: RecordRemoteWithAll[],
  options?: { apiBaseUrl?: string },
): RecordModel[] => {
  return records.map((r) => {
    return {
      id: r.id,
      mouvement: r.movement,
      poids: r.weight,
      dateLieu: r.dateLocation,
      athleteName: r.athleteName,
      disciplineId: r.disciplineId,
      sexe: r.sex,
      catAgeId: r.ageCategoryId,
      catPoidId: r.weightCategoryId,
      organisationId: r.organisationId,
      sheetDate: r.sheetDate,
      organisationName: r.organisation?.name ?? null,
      recordLabel: r.organisation?.recordLabel ?? null,
      recordColor: r.organisation?.recordColor ?? null,
      recordBadgeUrl: resolvePublicAssetUrl(
        r.organisation?.recordBadgeUrl,
        options?.apiBaseUrl,
      ),
      discipline: {
        ...disciplineMetadata(r.discipline),
        id: r.discipline.id,
        code: r.discipline.code,
        competitionFormat: r.discipline.competitionFormat,
        nom: r.discipline.name,
        organisationId: r.discipline.organisationId,
        displayOrder: r.discipline.displayOrder,
      },
      catAge: {
        ...ageCategoryMetadata(r.ageCategory),
        id: r.ageCategory.id,
        code: r.ageCategory.code,
        ageMax: r.ageCategory.ageMax,
        ageMin: r.ageCategory.ageMin,
        nom: r.ageCategory.name,
        nomRecord: r.ageCategory.recordName,
        disciplineId: r.ageCategory.disciplineId,
        organisationId: r.ageCategory.organisationId,
        isOpen: r.ageCategory.isOpen,
        displayOrder: r.ageCategory.displayOrder,
      },
      catPoid: {
        ...weightCategoryMetadata(r.weightCategory),
        id: r.weightCategory.id,
        isJunior: r.weightCategory.isJunior,
        nom: r.weightCategory.name,
        poidMax: r.weightCategory.maxWeight,
        poidMin: r.weightCategory.minWeight,
        sex: r.weightCategory.sex,
        organisationId: r.weightCategory.organisationId,
        displayOrder: r.weightCategory.displayOrder,
      },
    };
  });
};

export const levelRemoteToLevelLocalCreateOnly = (
  levels: LevelRemoteWithAll[],
): NiveauxModel[] => {
  return levels.map((l) => {
    return {
      catAgeId: l.ageCategoryId,
      catPoidId: l.weightCategoryId,
      disciplineId: l.disciplineId,
      totalMin: l.minTotal,
      totalMax: l.maxTotal,
      nom: l.name,
      id: l.id,
      organisationId: l.organisationId,
      sheetDate: l.sheetDate,
    };
  });
};

export const recordLocalToRecordRemote = (
  records: RecordModel[],
): RecordRemoteWithAll[] => {
  return records.map((r) => {
    return {
      id: r.id,
      movement: r.mouvement,
      weight: r.poids,
      dateLocation: r.dateLieu,
      athleteName: r.athleteName,
      disciplineId: r.disciplineId,
      sex: r.sexe,
      ageCategoryId: r.catAgeId,
      weightCategoryId: r.catPoidId,
      organisationId: r.organisationId,
      sheetDate: r.sheetDate ?? "",
      discipline: {
        ...disciplineMetadata({}),
        id: r.discipline?.id ?? 0,
        code: r.discipline?.code ?? "",
        competitionFormat: r.discipline?.competitionFormat ?? "FULL_POWER",
        name: r.discipline?.nom ?? "",
        organisationId: r.discipline?.organisationId,
        displayOrder: r.discipline?.displayOrder,
      },
      ageCategory: {
        ...ageCategoryMetadata({}),
        id: r.catAge?.id ?? 0,
        code: r.catAge?.code ?? "",
        ageMax: r.catAge?.ageMax ?? 0,
        ageMin: r.catAge?.ageMin ?? 0,
        name: r.catAge?.nom ?? "",
        recordName: r.catAge?.nomRecord ?? "",
        disciplineId: r.catAge?.disciplineId ?? 0,
        organisationId: r.catAge?.organisationId,
        isOpen: r.catAge?.isOpen,
        displayOrder: r.catAge?.displayOrder,
      },
      weightCategory: {
        ...weightCategoryMetadata({}),
        id: r.catPoid?.id ?? 0,
        isJunior: r.catPoid?.isJunior ?? false,
        name: r.catPoid?.nom ?? "",
        maxWeight: r.catPoid?.poidMax ?? 0,
        minWeight: r.catPoid?.poidMin ?? 0,
        sex: r.catPoid?.sex ?? "M",
        organisationId: r.catPoid?.organisationId,
        displayOrder: r.catPoid?.displayOrder,
      },
      organisation: r.organisationId
        ? {
            id: r.organisationId,
            name: r.organisationName ?? "",
            code: null as string | null,
            countryCode: null as string | null,
            recordLabel: r.recordLabel ?? null,
            recordColor: r.recordColor ?? null,
            recordBadgeUrl: r.recordBadgeUrl ?? null,
            parentId: null as string | null,
          }
        : null,
    };
  });
};

export const levelRemoteToLevelLocal = (
  levels: LevelRemoteWithAll[],
): NiveauxModel[] => {
  return levels.map((l) => {
    return {
      catAgeId: l.ageCategoryId,
      catPoidId: l.weightCategoryId,
      disciplineId: l.disciplineId,
      totalMin: l.minTotal,
      totalMax: l.maxTotal,
      nom: l.name,
      id: l.id,
      organisationId: l.organisationId,
      sheetDate: l.sheetDate,
      catAge: {
        ...ageCategoryMetadata(l.ageCategory),
        id: l.ageCategory.id,
        code: l.ageCategory.code,
        ageMax: l.ageCategory.ageMax,
        ageMin: l.ageCategory.ageMin,
        nom: l.ageCategory.name,
        nomRecord: l.ageCategory.recordName,
        disciplineId: l.ageCategory.disciplineId,
        organisationId: l.ageCategory.organisationId,
        isOpen: l.ageCategory.isOpen,
        displayOrder: l.ageCategory.displayOrder,
      },
      catPoid: {
        ...weightCategoryMetadata(l.weightCategory),
        id: l.weightCategory.id,
        isJunior: l.weightCategory.isJunior,
        nom: l.weightCategory.name,
        poidMax: l.weightCategory.maxWeight,
        poidMin: l.weightCategory.minWeight,
        sex: l.weightCategory.sex,
        organisationId: l.weightCategory.organisationId,
        displayOrder: l.weightCategory.displayOrder,
      },
      discipline: {
        ...disciplineMetadata(l.discipline),
        id: l.discipline.id,
        code: l.discipline.code,
        competitionFormat: l.discipline.competitionFormat,
        nom: l.discipline.name,
        organisationId: l.discipline.organisationId,
        displayOrder: l.discipline.displayOrder,
      },
    };
  });
};

export const levelLocalToLevelRemote = (
  levels: NiveauxModel[],
): LevelRemoteWithAll[] => {
  return levels.map((l) => {
    return {
      id: l.id,
      minTotal: l.totalMin,
      maxTotal: l.totalMax,
      name: l.nom,
      ageCategoryId: l.catAgeId,
      weightCategoryId: l.catPoidId,
      disciplineId: l.disciplineId,
      organisationId: l.organisationId,
      sheetDate: l.sheetDate ?? "",
      ageCategory: {
        ...ageCategoryMetadata({}),
        id: l.catAge?.id ?? 0,
        code: l.catAge?.code ?? "",
        ageMax: l.catAge?.ageMax ?? 0,
        ageMin: l.catAge?.ageMin ?? 0,
        name: l.catAge?.nom ?? "",
        recordName: l.catAge?.nomRecord ?? "",
        disciplineId: l.catAge?.disciplineId ?? 0,
        organisationId: l.catAge?.organisationId,
        isOpen: l.catAge?.isOpen,
        displayOrder: l.catAge?.displayOrder,
      },
      weightCategory: {
        ...weightCategoryMetadata({}),
        id: l.catPoid?.id ?? 0,
        isJunior: l.catPoid?.isJunior ?? false,
        name: l.catPoid?.nom ?? "",
        maxWeight: l.catPoid?.poidMax ?? 0,
        minWeight: l.catPoid?.poidMin ?? 0,
        sex: l.catPoid?.sex ?? "M",
        organisationId: l.catPoid?.organisationId,
        displayOrder: l.catPoid?.displayOrder,
      },
      discipline: {
        ...disciplineMetadata({}),
        id: l.discipline?.id ?? 0,
        code: l.discipline?.code ?? "",
        competitionFormat: l.discipline?.competitionFormat ?? "FULL_POWER",
        name: l.discipline?.nom ?? "",
        organisationId: l.discipline?.organisationId,
        displayOrder: l.discipline?.displayOrder,
      },
    };
  });
};

export const groupModelToGroupWithAll = (
  model: GroupModel,
  meetId: string,
): GroupWithAll => {
  return {
    customIsOver:
      model.customIsOver ??
      customDisciplineGroupIsOver(model.customDisciplineGroup),
    referee1: model.assesseur1,
    referee2: model.assesseur2,
    centralReferee: model.chefPlateau,
    id: model.id,
    name: model.nom,
    observations: model.observations,
    secretary: model.secretaire,
    athletes: model.athletes
      ? model.athletes.map((a) => athleteModelToAthleteWithAll(a))
      : [],
    meet_id: meetId,
    selected: model.selected ?? false,
    share: model.share,
    competitionFormat: model.competitionFormat || "FULL_POWER",
    customDisciplineGroupId: model.customDisciplineGroupId ?? null,
    customDisciplineGroup: model.customDisciplineGroup,
    groupNumber: model.groupNumber,
    plateauNumber: model.plateauNumber,
    jury: model.jury,
  };
};

export const athleteWithAllToAthleteModel = (
  athlete: Omit<
    AthleteWithAll,
    "ageCategory" | "weightCategory" | "discipline" | "powerscoutLifterId"
  > & {
    ageCategory: Pick<
      AthleteWithAll["ageCategory"],
      | "id"
      | "name"
      | "code"
      | "recordName"
      | "ageMin"
      | "ageMax"
      | "disciplineId"
    > &
      Partial<AthleteWithAll["ageCategory"]>;
    weightCategory: Pick<
      AthleteWithAll["weightCategory"],
      "id" | "name" | "minWeight" | "maxWeight" | "sex" | "isJunior"
    > &
      Partial<AthleteWithAll["weightCategory"]>;
    discipline: Pick<
      AthleteWithAll["discipline"],
      "id" | "name" | "code" | "competitionFormat"
    > &
      Partial<AthleteWithAll["discipline"]>;
    powerscoutLifterId?: number | null;
  },
): AthletesModel => {
  return {
    powerscoutBests: athlete.powerscoutBests,
    bench1: athlete.bench1,
    bench1Status: athlete.bench1Status,
    bench2: athlete.bench2,
    bench2Status: athlete.bench2Status,
    bench3: athlete.bench3,
    bench3Status: athlete.bench3Status,
    squat1: athlete.squat1,
    squat1Status: athlete.squat1Status,
    squat2: athlete.squat2,
    squat2Status: athlete.squat2Status,
    squat3: athlete.squat3,
    squat3Status: athlete.squat3Status,
    deadlift1: athlete.deadlift1,
    deadlift1Status: athlete.deadlift1Status,
    deadlift2: athlete.deadlift2,
    deadlift2Status: athlete.deadlift2Status,
    deadlift3: athlete.deadlift3,
    deadlift3Status: athlete.deadlift3Status,
    cales: athlete.cales,
    catAgeId: athlete.ageCategoryId,
    catPoidId: athlete.weightCategoryId,
    club: athlete.club,
    dateNaissance: athlete.birthDate,
    disciplineId: athlete.disciplineId,
    group_id: athlete.group_id,
    hauteurDc: athlete.benchHeight,
    hauteurSecu: athlete.secuHeight,
    hauteurSquat: athlete.squatHeight,
    id: athlete.id,
    indice: athlete.points,
    isDisqualified: athlete.isDisqualified,
    licence: athlete.licence,
    lot: athlete.lot,
    nom: athlete.lastname,
    pdc: athlete.bodyweight,
    placement: athlete.rank,
    positionRack: athlete.rackPosition,
    prenom: athlete.firstname,
    sexe: athlete.sex,
    totalAnnonced: athlete.totalAnnonced,
    totalkg: athlete.totalkg,
    squat1Auto: athlete.squat1Auto,
    squat2Auto: athlete.squat2Auto,
    squat3Auto: athlete.squat3Auto,
    squat1Overturn: athlete.squat1Overturn,
    squat2Overturn: athlete.squat2Overturn,
    squat3Overturn: athlete.squat3Overturn,
    bench1Auto: athlete.bench1Auto,
    bench2Auto: athlete.bench2Auto,
    bench3Auto: athlete.bench3Auto,
    bench1Overturn: athlete.bench1Overturn,
    bench2Overturn: athlete.bench2Overturn,
    bench3Overturn: athlete.bench3Overturn,
    deadlift1Auto: athlete.deadlift1Auto,
    deadlift2Auto: athlete.deadlift2Auto,
    deadlift3Auto: athlete.deadlift3Auto,
    deadlift1Overturn: athlete.deadlift1Overturn,
    deadlift2Overturn: athlete.deadlift2Overturn,
    deadlift3Overturn: athlete.deadlift3Overturn,
    squat1Changes: 0,
    bench1Changes: 0,
    bench3Changes: 0,
    deadlift1Changes: 0,
    deadlift3Changes: 0,
    bench1ChiefColor: athlete.bench1ChiefColor,
    bench2ChiefColor: athlete.bench2ChiefColor,
    bench3ChiefColor: athlete.bench3ChiefColor,
    bench1LeftColor: athlete.bench1LeftColor,
    bench2LeftColor: athlete.bench2LeftColor,
    bench3LeftColor: athlete.bench3LeftColor,
    bench1RightColor: athlete.bench1RightColor,
    bench2RightColor: athlete.bench2RightColor,
    bench3RightColor: athlete.bench3RightColor,
    bench1OverturnColor: athlete.bench1OverturnColor,
    bench2OverturnColor: athlete.bench2OverturnColor,
    bench3OverturnColor: athlete.bench3OverturnColor,
    squat1ChiefColor: athlete.squat1ChiefColor,
    squat2ChiefColor: athlete.squat2ChiefColor,
    squat3ChiefColor: athlete.squat3ChiefColor,
    squat1LeftColor: athlete.squat1LeftColor,
    squat2LeftColor: athlete.squat2LeftColor,
    squat3LeftColor: athlete.squat3LeftColor,
    squat1RightColor: athlete.squat1RightColor,
    squat2RightColor: athlete.squat2RightColor,
    squat3RightColor: athlete.squat3RightColor,
    squat1OverturnColor: athlete.squat1OverturnColor,
    squat2OverturnColor: athlete.squat2OverturnColor,
    squat3OverturnColor: athlete.squat3OverturnColor,
    deadlift1ChiefColor: athlete.deadlift1ChiefColor,
    deadlift2ChiefColor: athlete.deadlift2ChiefColor,
    deadlift3ChiefColor: athlete.deadlift3ChiefColor,
    deadlift1LeftColor: athlete.deadlift1LeftColor,
    deadlift2LeftColor: athlete.deadlift2LeftColor,
    deadlift3LeftColor: athlete.deadlift3LeftColor,
    deadlift1RightColor: athlete.deadlift1RightColor,
    deadlift2RightColor: athlete.deadlift2RightColor,
    deadlift3RightColor: athlete.deadlift3RightColor,
    deadlift1OverturnColor: athlete.deadlift1OverturnColor,
    deadlift2OverturnColor: athlete.deadlift2OverturnColor,
    deadlift3OverturnColor: athlete.deadlift3OverturnColor,
    catAge: {
      ...ageCategoryMetadata(athlete.ageCategory),
      ageMax: athlete.ageCategory.ageMax,
      ageMin: athlete.ageCategory.ageMin,
      code: athlete.ageCategory.code,
      id: athlete.ageCategory.id,
      nom: athlete.ageCategory.name,
      nomRecord: athlete.ageCategory.recordName,
      disciplineId: athlete.disciplineId,
      organisationId: athlete.ageCategory.organisationId,
      isOpen: athlete.ageCategory.isOpen,
      displayOrder: athlete.ageCategory.displayOrder,
    },
    catPoid: {
      ...weightCategoryMetadata(athlete.weightCategory),
      id: athlete.weightCategory.id,
      isJunior: athlete.weightCategory.isJunior,
      nom: athlete.weightCategory.name,
      poidMax: athlete.weightCategory.maxWeight,
      poidMin: athlete.weightCategory.minWeight,
      sex: athlete.weightCategory.sex,
      organisationId: athlete.weightCategory.organisationId,
      displayOrder: athlete.weightCategory.displayOrder,
    },
    discipline: {
      ...disciplineMetadata(athlete.discipline),
      code: athlete.discipline.code,
      id: athlete.discipline.id,
      nom: athlete.discipline.name,
      competitionFormat: getCompetitionFormatById(athlete.discipline.id),
      organisationId: athlete.discipline.organisationId,
      displayOrder: athlete.discipline.displayOrder,
    },
  };
};

export const athleteModelToAthleteWithAll = (
  model: AthletesModel,
): AthleteWithAll => {
  return {
    powerscoutBests: model.powerscoutBests,
    powerscoutLifterId: model.powerscoutBests?.powerscout_lifter_id ?? null,
    bench1: model.bench1,
    bench1Status: model.bench1Status,
    bench2: model.bench2,
    bench2Status: model.bench2Status,
    bench3: model.bench3,
    bench3Status: model.bench3Status,
    squat1: model.squat1,
    squat1Status: model.squat1Status,
    squat2: model.squat2,
    squat2Status: model.squat2Status,
    squat3: model.squat3,
    squat3Status: model.squat3Status,
    deadlift1: model.deadlift1,
    deadlift1Status: model.deadlift1Status,
    deadlift2: model.deadlift2,
    deadlift2Status: model.deadlift2Status,
    deadlift3: model.deadlift3,
    deadlift3Status: model.deadlift3Status,
    cales: model.cales,
    ageCategoryId: model.catAgeId,
    weightCategoryId: model.catPoidId,
    club: model.club,
    birthDate: model.dateNaissance,
    disciplineId: model.disciplineId,
    group_id: model.group_id,
    benchHeight: model.hauteurDc,
    secuHeight: model.hauteurSecu,
    squatHeight: model.hauteurSquat,
    id: model.id,
    points: model.indice,
    isDisqualified: model.isDisqualified,
    licence: model.licence,
    lot: model.lot,
    lastname: model.nom,
    bodyweight: model.pdc,
    rank: model.placement,
    rackPosition: model.positionRack,
    firstname: model.prenom,
    sex: model.sexe,
    totalAnnonced: model.totalAnnonced,
    totalkg: model.totalkg,
    squat1Auto: model.squat1Auto,
    squat2Auto: model.squat2Auto,
    squat3Auto: model.squat3Auto,
    squat1Overturn: model.squat1Overturn,
    squat2Overturn: model.squat2Overturn,
    squat3Overturn: model.squat3Overturn,
    bench1Auto: model.bench1Auto,
    bench2Auto: model.bench2Auto,
    bench3Auto: model.bench3Auto,
    bench1Overturn: model.bench1Overturn,
    bench2Overturn: model.bench2Overturn,
    bench3Overturn: model.bench3Overturn,
    deadlift1Auto: model.deadlift1Auto,
    deadlift2Auto: model.deadlift2Auto,
    deadlift3Auto: model.deadlift3Auto,
    deadlift1Overturn: model.deadlift1Overturn,
    deadlift2Overturn: model.deadlift2Overturn,
    deadlift3Overturn: model.deadlift3Overturn,
    bench1ChiefColor: model.bench1ChiefColor,
    bench2ChiefColor: model.bench2ChiefColor,
    bench3ChiefColor: model.bench3ChiefColor,
    bench1LeftColor: model.bench1LeftColor,
    bench2LeftColor: model.bench2LeftColor,
    bench3LeftColor: model.bench3LeftColor,
    bench1RightColor: model.bench1RightColor,
    bench2RightColor: model.bench2RightColor,
    bench3RightColor: model.bench3RightColor,
    bench1OverturnColor: model.bench1OverturnColor,
    bench2OverturnColor: model.bench2OverturnColor,
    bench3OverturnColor: model.bench3OverturnColor,
    squat1ChiefColor: model.squat1ChiefColor,
    squat2ChiefColor: model.squat2ChiefColor,
    squat3ChiefColor: model.squat3ChiefColor,
    squat1LeftColor: model.squat1LeftColor,
    squat2LeftColor: model.squat2LeftColor,
    squat3LeftColor: model.squat3LeftColor,
    squat1RightColor: model.squat1RightColor,
    squat2RightColor: model.squat2RightColor,
    squat3RightColor: model.squat3RightColor,
    squat1OverturnColor: model.squat1OverturnColor,
    squat2OverturnColor: model.squat2OverturnColor,
    squat3OverturnColor: model.squat3OverturnColor,
    deadlift1ChiefColor: model.deadlift1ChiefColor,
    deadlift2ChiefColor: model.deadlift2ChiefColor,
    deadlift3ChiefColor: model.deadlift3ChiefColor,
    deadlift1LeftColor: model.deadlift1LeftColor,
    deadlift2LeftColor: model.deadlift2LeftColor,
    deadlift3LeftColor: model.deadlift3LeftColor,
    deadlift1RightColor: model.deadlift1RightColor,
    deadlift2RightColor: model.deadlift2RightColor,
    deadlift3RightColor: model.deadlift3RightColor,
    deadlift1OverturnColor: model.deadlift1OverturnColor,
    deadlift2OverturnColor: model.deadlift2OverturnColor,
    deadlift3OverturnColor: model.deadlift3OverturnColor,
    ageCategory: {
      ...ageCategoryMetadata({}),
      ageMax: model.catAge?.ageMax ?? 0,
      ageMin: model.catAge?.ageMin ?? 0,
      code: model.catAge?.code ?? "",
      id: model.catAge?.id ?? 0,
      name: model.catAge?.nom ?? "",
      recordName: model.catAge?.nomRecord ?? "",
      disciplineId: model.catAge?.disciplineId ?? 0,
    },
    weightCategory: {
      ...weightCategoryMetadata({}),
      id: model.catPoid?.id ?? 0,
      isJunior: model.catPoid?.isJunior ?? false,
      name: model.catPoid?.nom ?? "",
      maxWeight: model.catPoid?.poidMax ?? 0,
      minWeight: model.catPoid?.poidMin ?? 0,
      sex: model.catPoid?.sex ?? "",
    },
    discipline: {
      ...disciplineMetadata({}),
      code: model.discipline?.code ?? "",
      id: model.discipline?.id ?? 0,
      name: model.discipline?.nom ?? "",
      competitionFormat: model.discipline?.competitionFormat ?? "",
    },
  };
};

export const meetModelToMeetWithAll = (payload: MeetModel): MeetWithAll => {
  // Guard incomplete live payloads (null meet) so a bad socket emit cannot crash
  // Electron's main process via an uncaught exception.
  // Ligue is legacy (replaced by organisationId); keep a soft fallback for remote
  // Meet.ligue_id which remains required in Prisma until a full migration drops it.
  if (!payload?.guid) {
    throw new Error("meetModelToMeetWithAll: meet payload is missing guid");
  }
  const ligueId = payload.ligue?.id ?? payload.ligue_id ?? 0;
  const ligueName = payload.ligue?.nom ?? "";
  return {
    localWidgetsURLs: payload.localWidgetsURLs,
    licenseKey: payload.licenseKey,
    records: payload.records,
    niveaux: payload.niveaux,
    city: payload.ville,
    created_at: payload.created_at,
    enddate: payload.enddate,
    id: payload.guid,
    isOver: false,
    isPublic: false,
    // Propagate competition organiser so layout InjectedData.organisation can resolve
    organisationId: payload.organisationId,
    ligue: { id: ligueId, name: ligueName },
    ligue_id: payload.ligue_id ?? ligueId,
    name: payload.name,
    startdate: payload.startdate,
    streamUrl: payload.streamUrl,
    isNational: payload.isNational ?? false,
    // Propagate meet scoring formula so live clients know GL vs DOTS
    pointsFormula:
      (payload as { pointsFormula?: string }).pointsFormula ?? "GL",
    plateauStreams: (payload.plateauStreams ?? []).map((s) => ({
      id: s.id,
      plateauNumber: s.plateauNumber,
      url: s.url,
    })),
    machineId: payload.machineId,
    currentCustomAthleteId: payload.currentCustomAthleteId ?? null,
    groups: (payload.groups ?? []).map((g) => {
      return {
        centralReferee: g.chefPlateau,
        id: g.id,
        meet_id: payload.guid,
        name: g.nom,
        observations: g.observations,
        referee1: g.assesseur1,
        referee2: g.assesseur2,
        secretary: g.secretaire,
        selected: g.selected,
        share: g.share,
        jury: g.jury,
        plateauNumber: g.plateauNumber,
        groupNumber: g.groupNumber,
        competitionFormat: g.competitionFormat || "FULL_POWER",
        customDisciplineGroupId: g.customDisciplineGroupId ?? null,
        customIsOver:
          g.customIsOver ??
          customDisciplineGroupIsOver(g.customDisciplineGroup),
        customDisciplineGroup:
          (
            g as {
              customDisciplineGroup?: unknown;
            }
          ).customDisciplineGroup ?? null,
        athletes: g.athletes
          ? g.athletes.map((a) => {
              return {
                powerscoutBests: a.powerscoutBests,
                discipline: {
                  name: a.discipline ? a.discipline.nom : "",
                  code: a.discipline ? a.discipline.code : "",
                  id: a.discipline ? a.discipline.id : -1,
                },
                ageCategory: {
                  ...ageCategoryMetadata({}),
                  disciplineId: a.disciplineId,
                  id: a.catAgeId,
                  name: a.catAge ? a.catAge.nom : "",
                  code: a.catAge ? a.catAge.code : "",
                  recordName: a.catAge ? a.catAge.nomRecord : "",
                  ageMin: a.catAge ? a.catAge.ageMin : 0,
                  ageMax: a.catAge ? a.catAge.ageMax : 0,
                },
                weightCategory: {
                  ...weightCategoryMetadata({}),
                  id: a.catPoidId,
                  name: a.catPoid ? a.catPoid.nom : "",
                  minWeight: a.catPoid ? a.catPoid.poidMin : 0,
                  maxWeight: a.catPoid ? a.catPoid.poidMax : 0,
                  isJunior: a.catPoid ? a.catPoid.isJunior : false,
                  sex: a.catPoid ? a.catPoid.sex : "",
                },
                group_id: g.id,
                lastname: a.nom,
                firstname: a.prenom,
                club: a.club,
                licence: a.licence,
                sex: a.sexe,
                birthDate: new Date(a.dateNaissance),
                bodyweight: a.pdc,
                ageCategoryId: a.catAgeId,
                weightCategoryId: a.catPoidId,
                lot: a.lot,
                squat1: a.squat1,
                squat1Status: a.squat1Status,
                squat2: a.squat2,
                squat2Status: a.squat2Status,
                squat3: a.squat3,
                squat3Status: a.squat3Status,
                bench1: a.bench1,
                bench1Status: a.bench1Status,
                bench2: a.bench2,
                bench2Status: a.bench2Status,
                bench3: a.bench3,
                bench3Status: a.bench3Status,
                deadlift1: a.deadlift1,
                deadlift1Status: a.deadlift1Status,
                deadlift2: a.deadlift2,
                deadlift2Status: a.deadlift2Status,
                deadlift3: a.deadlift3,
                deadlift3Status: a.deadlift3Status,
                totalkg: a.totalkg,
                totalAnnonced: a.totalAnnonced,
                rank: a.placement,
                squatHeight: a.hauteurSquat,
                benchHeight: a.hauteurDc,
                secuHeight: a.hauteurSecu,
                cales: a.cales,
                rackPosition: a.positionRack,
                points: a.indice,
                disciplineId: a.disciplineId,
                id: a.id,
                isDisqualified: a.isDisqualified,
                squat1Auto: a.squat1Auto,
                squat2Auto: a.squat2Auto,
                squat3Auto: a.squat3Auto,
                squat1Overturn: a.squat1Overturn,
                squat2Overturn: a.squat2Overturn,
                squat3Overturn: a.squat3Overturn,
                bench1Auto: a.bench1Auto,
                bench2Auto: a.bench2Auto,
                bench3Auto: a.bench3Auto,
                bench1Overturn: a.bench1Overturn,
                bench2Overturn: a.bench2Overturn,
                bench3Overturn: a.bench3Overturn,
                deadlift1Auto: a.deadlift1Auto,
                deadlift2Auto: a.deadlift2Auto,
                deadlift3Auto: a.deadlift3Auto,
                deadlift1Overturn: a.deadlift1Overturn,
                deadlift2Overturn: a.deadlift2Overturn,
                deadlift3Overturn: a.deadlift3Overturn,
                bench1ChiefColor: a.bench1ChiefColor,
                bench2ChiefColor: a.bench2ChiefColor,
                bench3ChiefColor: a.bench3ChiefColor,
                bench1LeftColor: a.bench1LeftColor,
                bench2LeftColor: a.bench2LeftColor,
                bench3LeftColor: a.bench3LeftColor,
                bench1RightColor: a.bench1RightColor,
                bench2RightColor: a.bench2RightColor,
                bench3RightColor: a.bench3RightColor,
                bench1OverturnColor: a.bench1OverturnColor,
                bench2OverturnColor: a.bench2OverturnColor,
                bench3OverturnColor: a.bench3OverturnColor,
                squat1ChiefColor: a.squat1ChiefColor,
                squat2ChiefColor: a.squat2ChiefColor,
                squat3ChiefColor: a.squat3ChiefColor,
                squat1LeftColor: a.squat1LeftColor,
                squat2LeftColor: a.squat2LeftColor,
                squat3LeftColor: a.squat3LeftColor,
                squat1RightColor: a.squat1RightColor,
                squat2RightColor: a.squat2RightColor,
                squat3RightColor: a.squat3RightColor,
                squat1OverturnColor: a.squat1OverturnColor,
                squat2OverturnColor: a.squat2OverturnColor,
                squat3OverturnColor: a.squat3OverturnColor,
                deadlift1ChiefColor: a.deadlift1ChiefColor,
                deadlift2ChiefColor: a.deadlift2ChiefColor,
                deadlift3ChiefColor: a.deadlift3ChiefColor,
                deadlift1LeftColor: a.deadlift1LeftColor,
                deadlift2LeftColor: a.deadlift2LeftColor,
                deadlift3LeftColor: a.deadlift3LeftColor,
                deadlift1RightColor: a.deadlift1RightColor,
                deadlift2RightColor: a.deadlift2RightColor,
                deadlift3RightColor: a.deadlift3RightColor,
                deadlift1OverturnColor: a.deadlift1OverturnColor,
                deadlift2OverturnColor: a.deadlift2OverturnColor,
                deadlift3OverturnColor: a.deadlift3OverturnColor,
              };
            })
          : [],
      };
    }),
  } as unknown as MeetWithAll;
};

export const meetWithAllToMeetModel = (
  payload: MeetWithAll,
  meetId: number,
): MeetModel => {
  return {
    id: meetId,
    localWidgetsURLs: payload.localWidgetsURLs,
    licenseKey: payload.licenseKey,
    records: payload.records,
    niveaux: payload.niveaux,
    ville: payload.city,
    created_at: payload.created_at,
    enddate: payload.enddate,
    guid: String(payload.id),
    machineId: payload.machineId,
    currentCustomAthleteId: payload.currentCustomAthleteId ?? null,
    ligue: {
      id: payload.ligue?.id,
      nom: payload.ligue?.name ?? "",
    },
    ligue_id: payload.ligue_id,
    name: payload.name,
    startdate: payload.startdate,
    streamUrl: payload.streamUrl ?? null,
    pointsFormula: payload.pointsFormula ?? "GL",
    isNational: payload.isNational ?? false,
    plateauStreams: (payload.plateauStreams ?? []).map((s) => ({
      id: s.id,
      plateauNumber: s.plateauNumber,
      url: s.url,
    })),
    groups: payload.groups.map((g) => {
      return {
        chefPlateau: g.centralReferee ?? null,
        id: g.id,
        meet_id: meetId,
        nom: g.name ?? "",
        observations: g.observations ?? null,
        assesseur1: g.referee1 ?? null,
        assesseur2: g.referee2 ?? null,
        secretaire: g.secretary ?? null,
        selected: Boolean(g.selected),
        share: g.share ?? null,
        competitionFormat: g.competitionFormat ?? "FULL_POWER",
        customDisciplineGroupId: g.customDisciplineGroupId ?? null,
        customDisciplineGroup: g.customDisciplineGroup,
        customIsOver:
          g.customIsOver ??
          customDisciplineGroupIsOver(g.customDisciplineGroup),
        groupNumber: g.groupNumber,
        plateauNumber: g.plateauNumber,
        jury: g.jury,
        athletes: (g.athletes ?? []).map((a): AthletesModel => {
          return {
            powerscoutBests: a.powerscoutBests,
            // === blocs de référence / catégories ===
            discipline: a.discipline
              ? {
                  id: a.discipline.id,
                  nom: a.discipline.name ?? "",
                  code: a.discipline.code ?? "",
                  competitionFormat:
                    a.discipline.competitionFormat ??
                    getCompetitionFormatById(a.disciplineId),
                }
              : undefined,
            disciplineId: a.disciplineId,

            catAgeId: a.ageCategory?.id ?? a.ageCategoryId,
            catAge: a.ageCategory
              ? {
                  ...ageCategoryMetadata(a.ageCategory),
                  id: a.ageCategory.id,
                  nom: a.ageCategory.name ?? "",
                  code: a.ageCategory.code ?? null,
                  nomRecord: a.ageCategory.recordName ?? null,
                  ageMin: a.ageCategory.ageMin ?? 0,
                  ageMax: a.ageCategory.ageMax ?? 0,
                  disciplineId: a.disciplineId,
                }
              : undefined,

            catPoidId: a.weightCategory?.id ?? a.weightCategoryId,
            catPoid: a.weightCategory
              ? {
                  ...weightCategoryMetadata(a.weightCategory),
                  id: a.weightCategory.id,
                  nom: a.weightCategory.name ?? "",
                  poidMin: a.weightCategory.minWeight ?? 0,
                  poidMax: a.weightCategory.maxWeight ?? 0,
                  isJunior: a.weightCategory.isJunior ?? false,
                  sex: a.weightCategory.sex ?? "",
                }
              : undefined,

            // === champs obligatoires ===
            group_id: g.id,

            // Auto / Overturn => toujours false
            squat1Auto: false,
            squat2Auto: false,
            squat3Auto: false,
            squat1Overturn: false,
            squat2Overturn: false,
            squat3Overturn: false,

            bench1Auto: false,
            bench2Auto: false,
            bench3Auto: false,
            bench1Overturn: false,
            bench2Overturn: false,
            bench3Overturn: false,

            deadlift1Auto: false,
            deadlift2Auto: false,
            deadlift3Auto: false,
            deadlift1Overturn: false,
            deadlift2Overturn: false,
            deadlift3Overturn: false,

            squat1LeftColor: a.squat1LeftColor ?? null,
            squat1ChiefColor: a.squat1ChiefColor ?? null,
            squat1RightColor: a.squat1RightColor ?? null,
            squat1OverturnColor: a.squat1OverturnColor ?? null,
            squat2LeftColor: a.squat2LeftColor ?? null,
            squat2ChiefColor: a.squat2ChiefColor ?? null,
            squat2RightColor: a.squat2RightColor ?? null,
            squat2OverturnColor: a.squat2OverturnColor ?? null,
            squat3LeftColor: a.squat3LeftColor ?? null,
            squat3ChiefColor: a.squat3ChiefColor ?? null,
            squat3RightColor: a.squat3RightColor ?? null,
            squat3OverturnColor: a.squat3OverturnColor ?? null,
            bench1LeftColor: a.bench1LeftColor ?? null,
            bench1ChiefColor: a.bench1ChiefColor ?? null,
            bench1RightColor: a.bench1RightColor ?? null,
            bench1OverturnColor: a.bench1OverturnColor ?? null,
            bench2LeftColor: a.bench2LeftColor ?? null,
            bench2ChiefColor: a.bench2ChiefColor ?? null,
            bench2RightColor: a.bench2RightColor ?? null,
            bench2OverturnColor: a.bench2OverturnColor ?? null,
            bench3LeftColor: a.bench3LeftColor ?? null,
            bench3ChiefColor: a.bench3ChiefColor ?? null,
            bench3RightColor: a.bench3RightColor ?? null,
            bench3OverturnColor: a.bench3OverturnColor ?? null,
            deadlift1LeftColor: a.deadlift1LeftColor ?? null,
            deadlift1ChiefColor: a.deadlift1ChiefColor ?? null,
            deadlift1RightColor: a.deadlift1RightColor ?? null,
            deadlift1OverturnColor: a.deadlift1OverturnColor ?? null,
            deadlift2LeftColor: a.deadlift2LeftColor ?? null,
            deadlift2ChiefColor: a.deadlift2ChiefColor ?? null,
            deadlift2RightColor: a.deadlift2RightColor ?? null,
            deadlift2OverturnColor: a.deadlift2OverturnColor ?? null,
            deadlift3LeftColor: a.deadlift3LeftColor ?? null,
            deadlift3ChiefColor: a.deadlift3ChiefColor ?? null,
            deadlift3RightColor: a.deadlift3RightColor ?? null,
            deadlift3OverturnColor: a.deadlift3OverturnColor ?? null,
            squat1Changes: 0,
            bench1Changes: 0,
            bench3Changes: 0,
            deadlift1Changes: 0,
            deadlift3Changes: 0,

            // === infos athlète ===
            nom: a.lastname,
            prenom: a.firstname,
            club: a.club,
            licence: a.licence,
            sexe: a.sex,
            dateNaissance: new Date(a.birthDate),
            pdc: a.bodyweight,
            lot: a.lot,

            squat1: a.squat1,
            squat1Status: a.squat1Status,
            squat2: a.squat2,
            squat2Status: a.squat2Status,
            squat3: a.squat3,
            squat3Status: a.squat3Status,

            bench1: a.bench1,
            bench1Status: a.bench1Status,
            bench2: a.bench2,
            bench2Status: a.bench2Status,
            bench3: a.bench3,
            bench3Status: a.bench3Status,

            deadlift1: a.deadlift1,
            deadlift1Status: a.deadlift1Status,
            deadlift2: a.deadlift2,
            deadlift2Status: a.deadlift2Status,
            deadlift3: a.deadlift3,
            deadlift3Status: a.deadlift3Status,

            totalkg: a.totalkg,
            totalAnnonced: a.totalAnnonced,
            placement: a.rank,
            hauteurSquat: a.squatHeight,
            hauteurDc: a.benchHeight,
            hauteurSecu: a.secuHeight,
            cales: a.cales,
            positionRack: a.rackPosition,
            indice: a.points,

            id: a.id,
            isDisqualified: a.isDisqualified,
          };
        }),
      };
    }),
  };
};

export const updateClassment = (
  athletes: AthletesModel[],
  updatedAthlete: AthletesModel,
  isNational = false,
) => {
  const categoryAthletes = athletes
    .filter((athlete) =>
      isSameRankingCategory(athlete, updatedAthlete, isNational),
    )
    .map((athlete) =>
      athlete.id === updatedAthlete.id ? updatedAthlete : athlete,
    );
  if (!categoryAthletes.some((athlete) => athlete.id === updatedAthlete.id)) {
    categoryAthletes.push(updatedAthlete);
  }
  const ranked = getAthletesByCategory(categoryAthletes, isNational);
  const placements = new Map(
    ranked.map((athlete) => [athlete.id, athlete.placement]),
  );
  for (const athlete of athletes) {
    if (placements.has(athlete.id))
      athlete.placement = placements.get(athlete.id)!;
  }
  updatedAthlete.placement = placements.get(updatedAthlete.id) ?? 0;
  return { updatedAthlete, athletes };
};

export const updateMeetClassment = (
  athletes: AthletesModel[],
  isNational = false,
) => {
  const placements = new Map(
    getAthletesByCategory(athletes, isNational).map((athlete) => [
      athlete.id,
      athlete.placement,
    ]),
  );
  for (const athlete of athletes)
    athlete.placement = placements.get(athlete.id) ?? 0;
  return athletes;
};
/** Rebuild live/API ranks when the competition ranking mode changes. */
export function recalculateMeetRankings(meet: MeetWithAll): void {
  const athletes = meet.groups.flatMap((group) => group.athletes);
  const placements = new Map(
    getAthletesByCategory(
      athletes.map(athleteWithAllToAthleteModel),
      meet.isNational,
    ).map((athlete) => [athlete.id, athlete.placement]),
  );
  for (const athlete of athletes)
    athlete.rank = placements.get(athlete.id) ?? 0;
}

export const getCatAgeN1 = (athlete: AthletesModel, catages: CatAgeModel[]) => {
  try {
    const dateNaissance = createDateAtNoon(athlete.dateNaissance)!;
    const ageN1 = new Date().getFullYear() - dateNaissance.getFullYear() + 1;
    const catAgeN1 = catages.find(
      (c) =>
        c.ageMax >= ageN1 &&
        c.ageMin <= ageN1 &&
        c.disciplineId === athlete.disciplineId,
    );
    return catAgeN1;
  } catch (error) {
    console.error("Error getting cat age N1:", error);
    return null;
  }
};

export const AgeCategoryToCatAgeModel = (
  ageCategory: AgeCategory,
): CatAgeModel => {
  return {
    ...ageCategoryMetadata(ageCategory),
    id: ageCategory.id,
    disciplineId: ageCategory.disciplineId,
    nom: ageCategory.name,
    nomRecord: ageCategory.recordName,
    code: ageCategory.code,
    ageMax: ageCategory.ageMax,
    ageMin: ageCategory.ageMin,
    organisationId: ageCategory.organisationId,
    isOpen: ageCategory.isOpen,
    displayOrder: ageCategory.displayOrder,
  };
};

export const MapWeightCategoryNameFAtoDC = (name: string): string => {
  const valuekg = name.match(/\d+/)?.[0];
  if (!valuekg) {
    return name;
  }
  const isMaxWeight = name.includes("+");
  if (isMaxWeight) {
    return `+${valuekg} Kg`;
  }
  return `${valuekg} Kg`;
};

/**
 * Creates a Date object with time set to noon (12:00:00) to avoid timezone issues.
 * This prevents date shifting when converting from string to Date due to timezone differences.
 *
 * @param dateInput - The date input (string, Date, or null/undefined)
 * @returns A Date object with time set to noon, or null if input is null/undefined
 * @example
 * createDateAtNoon("01-01-1999") // Returns Date object for 1999-01-01 12:00:00
 * createDateAtNoon(new Date("1999-01-01")) // Returns Date object for 1999-01-01 12:00:00
 * createDateAtNoon(null) // Returns null
 */
export const createDateAtNoon = (
  dateInput: string | Date | null | undefined,
): Date | null => {
  if (!dateInput) return null;

  const date = new Date(dateInput);
  // Set time to noon to avoid timezone issues
  date.setHours(12, 0, 0, 0);
  return date;
};

export const getDateFromString = (date: any): Date => {
  try {
    const formats = [
      "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
      "yyyy-MM-dd'T'HH:mm:ssxxx",
      "yyyy-MM-dd'T'HH:mm:ss",
      "yyyy-MM-dd",
      "dd/MM/yyyy",
    ];

    let parsedDate;
    for (const fmt of formats) {
      parsedDate = parse(date, fmt, new Date());
      if (isValid(parsedDate)) {
        break;
      }
    }

    if (!isValid(parsedDate)) {
      throw new Error("Invalid date format");
    }

    const dateStr = format(parsedDate ?? "", "yyyy-MM-dd'T'12:00:00");
    return new Date(dateStr);
  } catch (error) {
    console.error("Error getting date from string:", error);
    return new Date();
  }
};

export function getCellValue(
  row: any,
  column: number,
  type: "string" | "number" | "decimal" | "boolean" | "date",
): any {
  const cellValue = row.values[column];
  let value;

  if (
    typeof cellValue === "object" &&
    cellValue !== null &&
    "text" in cellValue
  ) {
    value = cellValue.text;
  } else {
    value = cellValue;
  }

  switch (type) {
    case "number":
      return parseFloat(value) || 0;
    case "decimal":
      return (parseFloat(value) || 0) as unknown as Decimal;
    case "boolean":
      return value === "true" || value === true;
    case "date":
      return value ? new Date(value) : null;
    case "string":
    default:
      return value ? value.toString().trim() : "";
  }
}

export const btoaFromArrayBuffer = (buffer: BufferSource) => {
  const binaryString = new TextDecoder("latin1").decode(buffer);
  return btoa(binaryString);
};

export const getStatusField = (field: string) => {
  switch (field) {
    case "squat1":
      return "squat1Status";
    case "squat2":
      return "squat2Status";
    case "squat3":
      return "squat3Status";
    case "bench1":
      return "bench1Status";
    case "bench2":
      return "bench2Status";
    case "bench3":
      return "bench3Status";
    case "deadlift1":
      return "deadlift1Status";
    case "deadlift2":
      return "deadlift2Status";
    case "deadlift3":
      return "deadlift3Status";
    default:
      return "squat1Status";
  }
};

export const getAttemptNumber = (lift: LiftField) => {
  return Number(lift?.slice(-1));
};
export const getTotalReal = (athlete: AthletesModel) => {
  return calculateTotal(athlete, false);
};

export const getTotalAnnonced = (athlete: AthletesModel) => {
  return calculateTotal(athlete, true);
};

export const groupDiff = (
  original: groups,
  updated: groups,
): Prisma.GroupUpdateInput => {
  const data: Prisma.GroupUpdateInput = {};

  // 1) Scalaires simples
  if (original.nom !== updated.nom) {
    data.name = updated.nom;
  }
  if (original.observations !== updated.observations) {
    data.observations = updated.observations;
  }
  if (original.secretaire !== updated.secretaire) {
    data.secretary = updated.secretaire;
  }
  if (original.assesseur1 !== updated.assesseur1) {
    data.referee1 = updated.assesseur1;
  }
  if (original.assesseur2 !== updated.assesseur2) {
    data.referee2 = updated.assesseur2;
  }
  if (original.chefPlateau !== updated.chefPlateau) {
    data.centralReferee = updated.chefPlateau;
  }
  if (original.plateauNumber !== updated.plateauNumber) {
    data.plateauNumber = updated.plateauNumber ?? undefined;
  }
  if (original.groupNumber !== updated.groupNumber) {
    data.groupNumber = updated.groupNumber ?? undefined;
  }
  if (original.jury !== updated.jury) {
    data.jury = updated.jury;
  }
  return data;
};

export const athleteDiff = (
  original: athletes,
  updated: athletes,
): Prisma.AthleteUpdateInput => {
  const data: Prisma.AthleteUpdateInput = {};

  // 1) Scalar string fields
  if (original === undefined || original.nom !== updated.nom) {
    data.lastname = updated.nom;
  }
  if (original === undefined || original.prenom !== updated.prenom) {
    data.firstname = updated.prenom;
  }
  if (original === undefined || original.club !== updated.club) {
    data.club = updated.club;
  }
  if (original === undefined || original.licence != updated.licence) {
    data.licence = updated.licence;
  }
  if (original === undefined || original.sexe != updated.sexe) {
    data.sex = updated.sexe;
  }
  if (original === undefined || original.positionRack != updated.positionRack) {
    data.rackPosition = updated.positionRack;
  }

  // 2) Date field
  if (
    original === undefined ||
    new Date(original.dateNaissance).getTime() !=
      new Date(updated.dateNaissance).getTime()
  ) {
    data.birthDate = updated.dateNaissance;
  }

  // 3) Numeric / nullable numeric fields
  if (original === undefined || original.pdc != updated.pdc) {
    data.bodyweight = updated.pdc;
  }
  if (original === undefined || original.lot != updated.lot) {
    data.lot = updated.lot;
  }
  if (original === undefined || original.placement != updated.placement) {
    data.rank = updated.placement;
  }
  if (original === undefined || original.hauteurSquat != updated.hauteurSquat) {
    data.squatHeight = updated.hauteurSquat;
  }
  if (original === undefined || original.hauteurDc != updated.hauteurDc) {
    data.benchHeight = updated.hauteurDc;
  }
  if (original === undefined || original.hauteurSecu != updated.hauteurSecu) {
    data.secuHeight = updated.hauteurSecu;
  }
  if (original === undefined || original.cales != updated.cales) {
    data.cales = updated.cales;
  }
  if (
    original === undefined ||
    Number(original.indice) !== Number(updated.indice)
  ) {
    data.points = updated.indice;
  }

  // 4) Decimal fields (as number|string|null)
  if (
    original === undefined ||
    Number(original.squat1) !== Number(updated.squat1)
  ) {
    data.squat1 = updated.squat1;
  }
  if (
    original === undefined ||
    Number(original.squat2) !== Number(updated.squat2)
  ) {
    data.squat2 = updated.squat2;
  }
  if (
    original === undefined ||
    Number(original.squat3) !== Number(updated.squat3)
  ) {
    data.squat3 = updated.squat3;
  }
  if (
    original === undefined ||
    Number(original.bench1) !== Number(updated.bench1)
  ) {
    data.bench1 = updated.bench1;
  }
  if (
    original === undefined ||
    Number(original.bench2) !== Number(updated.bench2)
  ) {
    data.bench2 = updated.bench2;
  }
  if (
    original === undefined ||
    Number(original.bench3) !== Number(updated.bench3)
  ) {
    data.bench3 = updated.bench3;
  }
  if (
    original === undefined ||
    Number(original.deadlift1) !== Number(updated.deadlift1)
  ) {
    data.deadlift1 = updated.deadlift1;
  }
  if (
    original === undefined ||
    Number(original.deadlift2) !== Number(updated.deadlift2)
  ) {
    data.deadlift2 = updated.deadlift2;
  }
  if (
    original === undefined ||
    Number(original.deadlift3) !== Number(updated.deadlift3)
  ) {
    data.deadlift3 = updated.deadlift3;
  }
  if (
    original === undefined ||
    Number(original.totalkg) !== Number(updated.totalkg)
  ) {
    data.totalkg = updated.totalkg;
  }
  if (
    original === undefined ||
    Number(original.totalAnnonced) !== Number(updated.totalAnnonced)
  ) {
    data.totalAnnonced = updated.totalAnnonced;
  }

  // 5) Status string fields
  if (original === undefined || original.squat1Status != updated.squat1Status) {
    data.squat1Status = updated.squat1Status;
  }
  if (original === undefined || original.squat2Status != updated.squat2Status) {
    data.squat2Status = updated.squat2Status;
  }
  if (original === undefined || original.squat3Status != updated.squat3Status) {
    data.squat3Status = updated.squat3Status;
  }
  if (original === undefined || original.bench1Status != updated.bench1Status) {
    data.bench1Status = updated.bench1Status;
  }
  if (original === undefined || original.bench2Status != updated.bench2Status) {
    data.bench2Status = updated.bench2Status;
  }
  if (original === undefined || original.bench3Status != updated.bench3Status) {
    data.bench3Status = updated.bench3Status;
  }
  if (
    original === undefined ||
    original.deadlift1Status != updated.deadlift1Status
  ) {
    data.deadlift1Status = updated.deadlift1Status;
  }
  if (
    original === undefined ||
    original.deadlift2Status != updated.deadlift2Status
  ) {
    data.deadlift2Status = updated.deadlift2Status;
  }
  if (
    original === undefined ||
    original.deadlift3Status != updated.deadlift3Status
  ) {
    data.deadlift3Status = updated.deadlift3Status;
  }

  if (
    original === undefined ||
    original.squat1LeftColor != updated.squat1LeftColor
  ) {
    data.squat1LeftColor = updated.squat1LeftColor;
  }
  if (
    original === undefined ||
    original.squat2LeftColor != updated.squat2LeftColor
  ) {
    data.squat2LeftColor = updated.squat2LeftColor;
  }
  if (
    original === undefined ||
    original.squat3LeftColor != updated.squat3LeftColor
  ) {
    data.squat3LeftColor = updated.squat3LeftColor;
  }
  if (
    original === undefined ||
    original.squat1ChiefColor != updated.squat1ChiefColor
  ) {
    data.squat1ChiefColor = updated.squat1ChiefColor;
  }
  if (
    original === undefined ||
    original.squat2ChiefColor != updated.squat2ChiefColor
  ) {
    data.squat2ChiefColor = updated.squat2ChiefColor;
  }
  if (
    original === undefined ||
    original.squat3ChiefColor != updated.squat3ChiefColor
  ) {
    data.squat3ChiefColor = updated.squat3ChiefColor;
  }
  if (
    original === undefined ||
    original.squat1RightColor != updated.squat1RightColor
  ) {
    data.squat1RightColor = updated.squat1RightColor;
  }
  if (
    original === undefined ||
    original.squat2RightColor != updated.squat2RightColor
  ) {
    data.squat2RightColor = updated.squat2RightColor;
  }
  if (
    original === undefined ||
    original.squat3RightColor != updated.squat3RightColor
  ) {
    data.squat3RightColor = updated.squat3RightColor;
  }

  if (
    original === undefined ||
    original.deadlift1LeftColor != updated.deadlift1LeftColor
  ) {
    data.deadlift1LeftColor = updated.deadlift1LeftColor;
  }
  if (
    original === undefined ||
    original.deadlift2LeftColor != updated.deadlift2LeftColor
  ) {
    data.deadlift2LeftColor = updated.deadlift2LeftColor;
  }
  if (
    original === undefined ||
    original.deadlift3LeftColor != updated.deadlift3LeftColor
  ) {
    data.deadlift3LeftColor = updated.deadlift3LeftColor;
  }
  if (
    original === undefined ||
    original.deadlift1ChiefColor != updated.deadlift1ChiefColor
  ) {
    data.deadlift1ChiefColor = updated.deadlift1ChiefColor;
  }
  if (
    original === undefined ||
    original.deadlift2ChiefColor != updated.deadlift2ChiefColor
  ) {
    data.deadlift2ChiefColor = updated.deadlift2ChiefColor;
  }
  if (
    original === undefined ||
    original.deadlift3ChiefColor != updated.deadlift3ChiefColor
  ) {
    data.deadlift3ChiefColor = updated.deadlift3ChiefColor;
  }
  if (
    original === undefined ||
    original.deadlift1RightColor != updated.deadlift1RightColor
  ) {
    data.deadlift1RightColor = updated.deadlift1RightColor;
  }
  if (
    original === undefined ||
    original.deadlift2RightColor != updated.deadlift2RightColor
  ) {
    data.deadlift2RightColor = updated.deadlift2RightColor;
  }
  if (
    original === undefined ||
    original.deadlift3RightColor != updated.deadlift3RightColor
  ) {
    data.deadlift3RightColor = updated.deadlift3RightColor;
  }

  if (
    original === undefined ||
    original.bench1LeftColor != updated.bench1LeftColor
  ) {
    data.bench1LeftColor = updated.bench1LeftColor;
  }
  if (
    original === undefined ||
    original.bench2LeftColor != updated.bench2LeftColor
  ) {
    data.bench2LeftColor = updated.bench2LeftColor;
  }
  if (
    original === undefined ||
    original.bench3LeftColor != updated.bench3LeftColor
  ) {
    data.bench3LeftColor = updated.bench3LeftColor;
  }
  if (
    original === undefined ||
    original.bench1ChiefColor != updated.bench1ChiefColor
  ) {
    data.bench1ChiefColor = updated.bench1ChiefColor;
  }
  if (
    original === undefined ||
    original.bench2ChiefColor != updated.bench2ChiefColor
  ) {
    data.bench2ChiefColor = updated.bench2ChiefColor;
  }
  if (
    original === undefined ||
    original.bench3ChiefColor != updated.bench3ChiefColor
  ) {
    data.bench3ChiefColor = updated.bench3ChiefColor;
  }
  if (
    original === undefined ||
    original.bench1RightColor != updated.bench1RightColor
  ) {
    data.bench1RightColor = updated.bench1RightColor;
  }
  if (
    original === undefined ||
    original.bench2RightColor != updated.bench2RightColor
  ) {
    data.bench2RightColor = updated.bench2RightColor;
  }
  if (
    original === undefined ||
    original.bench3RightColor != updated.bench3RightColor
  ) {
    data.bench3RightColor = updated.bench3RightColor;
  }

  if (
    original === undefined ||
    original.squat1OverturnColor != updated.squat1OverturnColor
  ) {
    data.squat1OverturnColor = updated.squat1OverturnColor;
  }
  if (
    original === undefined ||
    original.squat2OverturnColor != updated.squat2OverturnColor
  ) {
    data.squat2OverturnColor = updated.squat2OverturnColor;
  }
  if (
    original === undefined ||
    original.squat3OverturnColor != updated.squat3OverturnColor
  ) {
    data.squat3OverturnColor = updated.squat3OverturnColor;
  }

  if (
    original === undefined ||
    original.bench1OverturnColor != updated.bench1OverturnColor
  ) {
    data.bench1OverturnColor = updated.bench1OverturnColor;
  }
  if (
    original === undefined ||
    original.bench2OverturnColor != updated.bench2OverturnColor
  ) {
    data.bench2OverturnColor = updated.bench2OverturnColor;
  }
  if (
    original === undefined ||
    original.bench3OverturnColor != updated.bench3OverturnColor
  ) {
    data.bench3OverturnColor = updated.bench3OverturnColor;
  }

  if (
    original === undefined ||
    original.deadlift1OverturnColor != updated.deadlift1OverturnColor
  ) {
    data.deadlift1OverturnColor = updated.deadlift1OverturnColor;
  }
  if (
    original === undefined ||
    original.deadlift2OverturnColor != updated.deadlift2OverturnColor
  ) {
    data.deadlift2OverturnColor = updated.deadlift2OverturnColor;
  }
  if (
    original === undefined ||
    original.deadlift3OverturnColor != updated.deadlift3OverturnColor
  ) {
    data.deadlift3OverturnColor = updated.deadlift3OverturnColor;
  }

  // 6) Boolean flag
  if (
    original === undefined ||
    original.isDisqualified != updated.isDisqualified
  ) {
    data.isDisqualified = updated.isDisqualified;
  }

  if (original === undefined || original.squat1Auto != updated.squat1Auto) {
    data.squat1Auto = updated.squat1Auto;
  }
  if (original === undefined || original.squat2Auto != updated.squat2Auto) {
    data.squat2Auto = updated.squat2Auto;
  }
  if (original === undefined || original.squat3Auto != updated.squat3Auto) {
    data.squat3Auto = updated.squat3Auto;
  }

  if (original === undefined || original.bench1Auto != updated.bench1Auto) {
    data.bench1Auto = updated.bench1Auto;
  }
  if (original === undefined || original.bench2Auto != updated.bench2Auto) {
    data.bench2Auto = updated.bench2Auto;
  }
  if (original === undefined || original.bench3Auto != updated.bench3Auto) {
    data.bench3Auto = updated.bench3Auto;
  }

  if (
    original === undefined ||
    original.deadlift1Auto != updated.deadlift1Auto
  ) {
    data.deadlift1Auto = updated.deadlift1Auto;
  }
  if (
    original === undefined ||
    original.deadlift2Auto != updated.deadlift2Auto
  ) {
    data.deadlift2Auto = updated.deadlift2Auto;
  }
  if (
    original === undefined ||
    original.deadlift3Auto != updated.deadlift3Auto
  ) {
    data.deadlift3Auto = updated.deadlift3Auto;
  }

  if (
    original === undefined ||
    original.squat1Overturn != updated.squat1Overturn
  ) {
    data.squat1Overturn = updated.squat1Overturn;
  }
  if (
    original === undefined ||
    original.squat2Overturn != updated.squat2Overturn
  ) {
    data.squat2Overturn = updated.squat2Overturn;
  }
  if (
    original === undefined ||
    original.squat3Overturn != updated.squat3Overturn
  ) {
    data.squat3Overturn = updated.squat3Overturn;
  }

  if (
    original === undefined ||
    original.bench1Overturn != updated.bench1Overturn
  ) {
    data.bench1Overturn = updated.bench1Overturn;
  }
  if (
    original === undefined ||
    original.bench2Overturn != updated.bench2Overturn
  ) {
    data.bench2Overturn = updated.bench2Overturn;
  }
  if (
    original === undefined ||
    original.bench3Overturn != updated.bench3Overturn
  ) {
    data.bench3Overturn = updated.bench3Overturn;
  }

  if (
    original === undefined ||
    original.deadlift1Overturn != updated.deadlift1Overturn
  ) {
    data.deadlift1Overturn = updated.deadlift1Overturn;
  }
  if (
    original === undefined ||
    original.deadlift2Overturn != updated.deadlift2Overturn
  ) {
    data.deadlift2Overturn = updated.deadlift2Overturn;
  }
  if (
    original === undefined ||
    original.deadlift3Overturn != updated.deadlift3Overturn
  ) {
    data.deadlift3Overturn = updated.deadlift3Overturn;
  }

  // 7) Relations: connect if the FK changed
  if (original === undefined || original.group_id !== updated.group_id) {
    data.group = { connect: { id: updated.group_id } };
  }
  if (
    original === undefined ||
    original.disciplineId !== updated.disciplineId
  ) {
    data.discipline = { connect: { id: updated.disciplineId } };
  }
  if (original === undefined || original.catAgeId !== updated.catAgeId) {
    data.ageCategory = { connect: { id: updated.catAgeId } };
  }
  if (original === undefined || original.catPoidId !== updated.catPoidId) {
    data.weightCategory = { connect: { id: updated.catPoidId } };
  }

  if (original === undefined) {
    data.id = `${updated.id}/new`;
  }
  return data;
};

export const meetDiff = (
  original: meets,
  updated: meets,
): Prisma.MeetUpdateInput => {
  const data: Prisma.MeetUpdateInput = {};

  // champs scalaires simples
  if (original.name !== updated.name) {
    data.name = updated.name;
  }
  if (original.ville !== updated.ville) {
    // mapping de "ville" → "city"
    data.city = updated.ville;
  }

  // comparaison de dates par timestamp
  if (original.startdate.getTime() !== updated.startdate.getTime()) {
    data.startdate = updated.startdate;
  }
  if (original.enddate.getTime() !== updated.enddate.getTime()) {
    data.enddate = updated.enddate;
  }
  if (original.created_at.getTime() !== updated.created_at.getTime()) {
    data.created_at = updated.created_at;
  }

  if (original.streamUrl !== updated.streamUrl) {
    data.streamUrl = updated.streamUrl;
  }
  // relation ligue
  if (original.ligue_id !== updated.ligue_id) {
    data.ligue = {
      connect: { id: updated.ligue_id },
    };
  }
  if ((original.isNational ?? false) !== (updated.isNational ?? false)) {
    data.isNational = updated.isNational ?? false;
  }
  // Scoring formula (GL / DOTS) - remote Meet.pointsFormula
  if (
    (original as { pointsFormula?: string }).pointsFormula !==
    (updated as { pointsFormula?: string }).pointsFormula
  ) {
    data.pointsFormula =
      (updated as { pointsFormula?: string }).pointsFormula ?? "GL";
  }

  return data;
};

export const makeObject = <K extends string, V>(
  key: K,
  val: V,
): Record<K, V> => {
  return { [key]: val } as Record<K, V>;
};

const calculateTotal = (athlete: AthletesModel, includeAnnonced: boolean) => {
  let total = 0;
  let disqualified = false;

  const format = getCompetitionFormatById(athlete.disciplineId);
  const exercises = exercisesByFormat[format];

  for (const exercise of exercises) {
    let validLifts: number[] = [];
    let missedLifts: LiftField[] = [];
    for (const lift of exercise.lifts) {
      const status = athlete[getStatusField(lift)];
      if (
        status === LiftStatus.LIFT ||
        (includeAnnonced && status === LiftStatus.ANNONCED)
      ) {
        let value = Number(athlete[lift]);
        if (value) {
          validLifts.push(Number(value));
        }
      } else if (status === LiftStatus.NO_LIFT) {
        missedLifts.push(lift);
      }
      if (missedLifts.length === 3) {
        disqualified = true;
      }
    }
    if (validLifts.length > 0) {
      total += validLifts.reduce((a, b) => Math.max(Number(a), Number(b)));
    }
  }
  if (disqualified && !includeAnnonced) {
    return 0;
  }
  return total;
};

export const getAttemptStatus = (
  athlete: AthletesModel,
  currentLift: LiftField,
  attempt: number,
) => {
  const statusField =
    `${currentLift.replace(/[123]$/, "")}${attempt}Status` as keyof AthletesModel;
  return athlete[statusField] as LiftStatus;
};

export const getAttemptRefereeColors = (
  athlete: AthletesModel,
  currentLift: LiftField,
  attempt: number,
) => {
  const leftColorField =
    `${currentLift.replace(/[123]$/, "")}${attempt}LeftColor` as RefereeColorField;
  const chiefColorField =
    `${currentLift.replace(/[123]$/, "")}${attempt}ChiefColor` as RefereeColorField;
  const rightColorField =
    `${currentLift.replace(/[123]$/, "")}${attempt}RightColor` as RefereeColorField;
  const overturnColorField =
    `${currentLift.replace(/[123]$/, "")}${attempt}OverturnColor` as OverturnColorField;
  return {
    leftColor: athlete[leftColorField] ?? "",
    chiefColor: athlete[chiefColorField] ?? "",
    rightColor: athlete[rightColorField] ?? "",
    overturnColor: athlete[overturnColorField] ?? "",
  };
};

export const formatDateTime = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getAttemptOverturn = (
  athlete: AthletesModel,
  currentLift: LiftField,
  attempt: number,
) => {
  const overturnField =
    `${currentLift.replace(/[123]$/, "")}${attempt}Overturn` as OverturnField;
  return athlete[overturnField] ?? false;
};

export const getAttemptAuto = (
  athlete: AthletesModel,
  currentLift: LiftField,
  attempt: number,
) => {
  const autoField =
    `${currentLift.replace(/[123]$/, "")}${attempt}Auto` as AutoField;
  return athlete[autoField] ?? false;
};

export const getAttemptValue = (
  athlete: AthletesModel,
  currentLift: LiftField,
  attempt: number,
) => {
  const liftField =
    `${currentLift.replace(/[123]$/, "")}${attempt}` as keyof AthletesModel;
  return athlete[liftField];
};

/**
 * Resolves the minima row for a total, or the organisation default when none matches.
 *
 * @param totalkg - Athlete total in kg.
 * @param catAgeId - Age category id.
 * @param catPoidsId - Weight category id.
 * @param niveaux - Available minima rows for the federation level source.
 * @param disciplineId - Discipline id.
 * @param defaultLevelName - Organisation-configured fallback (null → "dépt.").
 * @returns Matching level row, or `{ nom: default }` when total is 0 / below minima.
 */
export const getNiveau = (
  totalkg: number,
  catAgeId: number,
  catPoidsId: number,
  niveaux: NiveauxModel[],
  disciplineId: number,
  defaultLevelName?: string | null,
) => {
  //we need to do this because ffforce consider -53 jnr to be -59 in open (does not make sense)
  const isOpen = ![1, 2, 8, 31, 32, 39].includes(catAgeId); // catAgeId is not open
  const isCatePoidJunior = [1, 10].includes(catPoidsId); // catPoidsId is junior

  if (isOpen && isCatePoidJunior) {
    catPoidsId = catPoidsId + 1; // if open and junior, go to the next catPoidsId
  }

  const niveau = niveaux.find(
    (n) =>
      n.catAgeId === catAgeId &&
      n.catPoidId === catPoidsId &&
      n.disciplineId === disciplineId &&
      totalkg >= n.totalMin &&
      totalkg <= n.totalMax,
  );
  // No total yet / below every minima → organisation default (ex. "dépt.")
  if (totalkg === 0 || !niveau) {
    return { nom: resolveDefaultLevelName(defaultLevelName) };
  }
  if (niveau.nom.toLowerCase() === "monde") {
    niveau.nom = "monde";
  }
  if (niveau.nom.toLowerCase() === "europe") {
    niveau.nom = "eur.";
  }
  return niveau;
};

/**
 * Finds the next level above the current level for a given athlete.
 * Returns the level with the lowest totalMin that is greater than the current level's totalMax.
 *
 * @param currentLevel - Current level (null / org default / legacy "0" → lowest minima).
 * @param catAgeId - Age category ID
 * @param catPoidsId - Weight category ID
 * @param niveaux - Array of all available levels
 * @param disciplineId - Discipline ID
 * @param defaultLevelName - Organisation-configured below-minima label (null → "dépt.").
 * @returns The next level above, or null if none exists
 */
export const getNextLevel = (
  currentLevel: NiveauxModel | { nom: string } | null,
  catAgeId: number,
  catPoidsId: number,
  niveaux: NiveauxModel[],
  disciplineId: number,
  defaultLevelName?: string | null,
): NiveauxModel | null => {
  const fallbackNom = resolveDefaultLevelName(defaultLevelName);
  // Org default / legacy "0" / no row → first (lowest) minima for the category
  if (
    !currentLevel ||
    !("totalMax" in currentLevel) ||
    currentLevel.nom === fallbackNom ||
    currentLevel.nom === DEFAULT_LEVEL_NAME_FALLBACK ||
    currentLevel.nom === "0"
  ) {
    const lowestLevel = niveaux
      .filter(
        (n) =>
          n.catAgeId === catAgeId &&
          n.catPoidId === catPoidsId &&
          n.disciplineId === disciplineId,
      )
      .sort((a, b) => a.totalMin - b.totalMin)[0];
    return lowestLevel || null;
  }

  // Find levels with totalMin greater than current level's totalMax
  const nextLevels = niveaux
    .filter(
      (n) =>
        n.catAgeId === catAgeId &&
        n.catPoidId === catPoidsId &&
        n.disciplineId === disciplineId &&
        n.totalMin > (currentLevel as NiveauxModel).totalMax,
    )
    .sort((a, b) => a.totalMin - b.totalMin);

  return nextLevels[0] || null;
};

export const sortAthletes = (athletes: AthletesModel[]) => {
  return athletes.sort((a, b) => {
    if (a.totalkg !== b.totalkg) return Number(b.totalkg) - Number(a.totalkg);
    else if (a.pdc !== b.pdc) return Number(b.pdc) - Number(a.pdc);
    else return a.lot - b.lot;
  });
};

export const sendGroupSelectData = (
  storedMeet: MeetWithAll | undefined,
  server: Server,
  syncMeta: SyncMeta,
  client?: Socket,
) => {
  if (storedMeet) {
    if (client) {
      client.emit(`${storedMeet.id}/select-group`, {
        payload: {
          groupId: storedMeet.groups.find((g) => g.selected === true)?.id,
        },
        syncMeta,
      });
    } else {
      server.to(storedMeet.id).emit(`${storedMeet.id}/select-group`, {
        payload: {
          groupId: storedMeet.groups.find((g) => g.selected === true)?.id,
        },
        syncMeta,
      });
    }
  }
};

export const sendCurrentAthleteWidgetData = (
  storedMeet: MeetWithAll | undefined,
  server: Server,
  syncMeta: SyncMeta,
  client?: Socket,
) => {
  if (storedMeet) {
    const currentGroup = storedMeet.groups.find((g) => g.selected);
    if (currentGroup) {
      const athletesModel = currentGroup.athletes.map((a) =>
        athleteWithAllToAthleteModel(a),
      );
      const lift = getCurrentLift(athletesModel);
      const athlete = getCurrentAthlete(athletesModel);
      if (lift) {
        const exercise = Exercises.find((e) => e.lifts.includes(lift)) || null;
        const attempt = parseInt(lift.slice(-1));
        let placementIfSuccessful: number | null = null;
        if (athlete) {
          placementIfSuccessful = getResultIfSuccessful(
            athlete,
            storedMeet.groups
              .flatMap((a) => a.athletes)
              .map((a) => athleteWithAllToAthleteModel(a)),
            getCurrentLift(athletesModel) ?? "squat1",
            storedMeet.isNational,
          ).placement;
        }

        if (client) {
          client.emit(`${storedMeet.id}/current-athlete`, {
            payload: {
              athlete,
              lift,
              exercise,
              attempt,
              placementIfSuccessful,
              meetName: storedMeet.name,
              startDate: storedMeet.startdate,
            },
            syncMeta,
          });
        } else {
          server.to(storedMeet.id).emit(`${storedMeet.id}/current-athlete`, {
            payload: {
              athlete,
              lift,
              exercise,
              attempt,
              placementIfSuccessful,
              meetName: storedMeet.name,
              startDate: storedMeet.startdate,
            },
            syncMeta,
          });
        }
      } else if (
        currentGroup.athletes.every(
          (a) =>
            a.squat1Status !== LiftStatus.ANNONCED &&
            a.squat2Status !== LiftStatus.ANNONCED &&
            a.squat3Status !== LiftStatus.ANNONCED &&
            a.bench1Status !== LiftStatus.ANNONCED &&
            a.bench2Status !== LiftStatus.ANNONCED &&
            a.bench3Status !== LiftStatus.ANNONCED &&
            a.deadlift1Status !== LiftStatus.ANNONCED &&
            a.deadlift2Status !== LiftStatus.ANNONCED &&
            a.deadlift3Status !== LiftStatus.ANNONCED,
        )
      ) {
        if (client) {
          client.emit(`${storedMeet.id}/current-athlete`, { syncMeta });
        } else {
          server
            .to(storedMeet.id)
            .emit(`${storedMeet.id}/current-athlete`, { syncMeta });
        }
      }
    }
  }
};

export const sendCurrentCategoryWidgetData = (
  storedMeet: MeetWithAll | undefined,
  server: Server,
  syncMeta: SyncMeta,
  client?: Socket,
) => {
  if (storedMeet) {
    const currentGroup = storedMeet.groups.find((g) => g.selected);
    if (currentGroup) {
      const athletesModel = storedMeet.groups
        .flatMap((g) => g.athletes)
        .map((a) => athleteWithAllToAthleteModel(a));
      const athlete = getCurrentAthlete(
        currentGroup.athletes.map((a) => athleteWithAllToAthleteModel(a)),
      );
      const lift = getCurrentLift(athletesModel);
      if (athlete) {
        let placementIfSuccessful: number | null = null;
        if (lift) {
          placementIfSuccessful = getResultIfSuccessful(
            athlete,
            storedMeet.groups
              .flatMap((a) => a.athletes)
              .map((a) => athleteWithAllToAthleteModel(a)),
            lift,
            storedMeet.isNational,
          ).placement;
        }
        const sameCategory = athletesModel.filter((a) =>
          isSameRankingCategory(a, athlete, storedMeet.isNational),
        );
        const sorted = getAthletesByCategory(
          sameCategory,
          storedMeet.isNational,
        );
        if (client) {
          client.emit(`${storedMeet.id}/current-category`, {
            payload: {
              athlete,
              sorted,
              placementIfSuccessful,
            },
            syncMeta,
          });
        } else {
          server.to(storedMeet.id).emit(`${storedMeet.id}/current-category`, {
            payload: {
              athlete,
              sorted,
              placementIfSuccessful,
            },
            syncMeta,
          });
        }
      } else if (
        currentGroup.athletes.every(
          (a) =>
            a.squat1Status !== LiftStatus.ANNONCED &&
            a.squat2Status !== LiftStatus.ANNONCED &&
            a.squat3Status !== LiftStatus.ANNONCED &&
            a.bench1Status !== LiftStatus.ANNONCED &&
            a.bench2Status !== LiftStatus.ANNONCED &&
            a.bench3Status !== LiftStatus.ANNONCED &&
            a.deadlift1Status !== LiftStatus.ANNONCED &&
            a.deadlift2Status !== LiftStatus.ANNONCED &&
            a.deadlift3Status !== LiftStatus.ANNONCED,
        )
      ) {
        if (client) {
          client.emit(`${storedMeet.id}/current-category`, { syncMeta });
        } else {
          server
            .to(storedMeet.id)
            .emit(`${storedMeet.id}/current-category`, { syncMeta });
        }
      }
    }
  }
};

/**
 * Calculates the announced ranking place for an athlete based on totalAnnonced.
 */
export const calculateAnnouncedPlace = (
  athlete: AthletesModel,
  allAthletes: AthletesModel[],
  isNational = false,
): number | null => {
  if (!athlete || athlete.isDisqualified) return null;

  const totalAnnonced = getTotalAnnonced(athlete);
  if (totalAnnonced === 0) return null;

  // Filter athletes in the same category
  const sameCategoryAthletes = allAthletes.filter(
    (a) =>
      !a.isDisqualified &&
      isSameRankingCategory(a, athlete, isNational) &&
      toNumber(a.totalkg) !== 0,
  );

  // Sort by totalAnnonced (descending), then by body weight (ascending), then by lot
  const sorted = [...sameCategoryAthletes].sort((a, b) => {
    const totalA = getTotalAnnonced(a);
    const totalB = getTotalAnnonced(b);
    if (totalA !== totalB) return totalB - totalA;
    if (Number(a.pdc) !== Number(b.pdc)) return Number(a.pdc) - Number(b.pdc);
    return a.lot - b.lot;
  });

  // Find the place
  const place = sorted.findIndex((a) => a.id === athlete.id);
  return place >= 0 ? place + 1 : null;
};

/**
 * Converts a Decimal or number value to a number, handling null and undefined.
 */
const toNumber = (
  value: Decimal | number | null | undefined,
): number | undefined => {
  if (value === null || value === undefined) return undefined;
  try {
    // Check if value is a Decimal instance
    if (value && typeof value === "object" && "toNumber" in value) {
      const decimalValue = value as Decimal;
      if (typeof decimalValue.toNumber === "function") {
        return decimalValue.toNumber();
      }
    }
    return Number(value);
  } catch (error) {
    console.error("Error converting value to number:", error, value);
    return undefined;
  }
};
/** Max persisted length for a private speaker note. Keep in sync with athlete-note-identity.ts. */
export const ATHLETE_NOTE_CONTENT_MAX_LENGTH = 8000;
/** Max identities accepted in a speaker-note lookup batch. */
export const ATHLETE_NOTE_LOOKUP_MAX_IDENTITIES = 200;
/** Fields used to resolve a speaker-note identity key. */
export type AthleteNoteIdentityInput = {
  licence?: string | null;
  lastname: string;
  firstname: string;
};
/**
 * Normalizes a federation licence: trim, uppercase, collapse inner whitespace.
 *
 * @param licence - Raw licence from the athlete record.
 * @returns Normalized licence, or empty string when missing.
 */
export const normalizeLicence = (licence?: string | null): string => {
  if (!licence) {
    return "";
  }
  return licence.trim().toUpperCase().replace(/\s+/g, " ");
};
/**
 * Normalizes a person name for identity matching (accents, case, spaces).
 *
 * @param value - Raw lastname or firstname.
 * @returns Normalized name, or empty string when missing.
 */
export const normalizePersonName = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
};
/**
 * Builds the durable identity key stored with a user athlete note.
 * Prefers licence when present; otherwise uses normalized lastname|firstname.
 *
 * @param identity - Athlete licence and names.
 * @returns Key such as `licence:123456` or `name:dupont|jean`.
 */
export const buildAthleteNoteIdentityKey = (
  identity: AthleteNoteIdentityInput,
): string => {
  const licence = normalizeLicence(identity.licence);
  if (licence) {
    return `licence:${licence}`;
  }
  const lastname = normalizePersonName(identity.lastname);
  const firstname = normalizePersonName(identity.firstname);
  return `name:${lastname}|${firstname}`;
};
/**
 * True when the identity can be stored (licence or both names after normalize).
 *
 * @param identity - Athlete licence and names.
 * @returns Whether a non-empty identity key can be built.
 */
export const hasAthleteNoteIdentity = (
  identity: AthleteNoteIdentityInput,
): boolean => {
  if (normalizeLicence(identity.licence)) {
    return true;
  }
  return Boolean(
    normalizePersonName(identity.lastname) &&
      normalizePersonName(identity.firstname),
  );
};
